/**
 * A participant's conference-intake record, read back: the badge photo and
 * details, the consent they signed (choices, initials, signature and the
 * exact text of the version they saw) and their survey answers.
 */
import { useEffect, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { extractErrorMessage } from '../../api/errors';
import {
  fetchIntakeImage,
  listParticipantIntakes,
  type ConsentDefinition,
  type ParticipantIntake,
  type SurveyAnswers,
  type SurveyQuestion,
} from '../../api/intake';
import { formatDate, formatDateTime } from '../../util/datetime';

const DASH = '—';

/** One answer as text, by question type. */
export function formatAnswer(q: SurveyQuestion, value: unknown): string {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return DASH;
  if (Array.isArray(value)) return value.join(', ');
  if (q.type === 'percent') return `${value}%`;
  if (q.type === 'percent_split' && typeof value === 'object') {
    const split = value as Record<string, unknown>;
    const parts = q.parts?.length ? q.parts : Object.keys(split);
    const shown = parts.filter((p) => split[p] != null).map((p) => `${p} ${split[p]}%`);
    return shown.length ? shown.join(' · ') : DASH;
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Same rule the server applies: a show_if question counts only when its
 *  condition was met. */
function visible(q: SurveyQuestion, answers: SurveyAnswers): boolean {
  return !q.show_if || answers[q.show_if.id] === q.show_if.equals;
}

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }} sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flex: { sm: '0 0 45%' } }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1, whiteSpace: 'pre-wrap' }}>
        {children}
      </Typography>
    </Stack>
  );
}

function ConsentText({ definition }: { definition: ConsentDefinition }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1, maxHeight: 360, overflow: 'auto' }}>
      <Typography variant="subtitle2">{definition.title}</Typography>
      {[definition.institution, definition.protocol_title, definition.protocol_director]
        .filter(Boolean)
        .map((line) => (
          <Typography key={line} variant="caption" color="text.secondary" display="block">
            {line}
          </Typography>
        ))}
      {definition.sections.map((s, i) => (
        <Box key={i} sx={{ mt: 1.5 }}>
          {s.heading && <Typography variant="subtitle2">{s.heading}</Typography>}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {s.body}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

/** Object URL for an intake image; revoked when the intake changes or on
 *  unmount. Missing / failed images just don't render. */
function useIntakeImage(
  tenantId: string,
  intakeId: string,
  which: 'signature' | 'badge-photo',
  has: boolean,
): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!has) return;
    let cancelled = false;
    let made: string | null = null;
    fetchIntakeImage(tenantId, intakeId, which)
      .then((u) => {
        if (cancelled) URL.revokeObjectURL(u);
        else {
          made = u;
          setUrl(u);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [tenantId, intakeId, which, has]);
  return url;
}

function IntakeRecord({ tenantId, intake }: { tenantId: string; intake: ParticipantIntake }) {
  const [showText, setShowText] = useState(false);
  const badge = useIntakeImage(tenantId, intake.id, 'badge-photo', intake.has_badge_photo);
  const signature = useIntakeImage(tenantId, intake.id, 'signature', intake.has_signature);
  const consent = intake.consent_form;
  const survey = intake.survey_form;
  const answers = intake.survey_answers ?? {};

  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="overline" color="text.secondary">
          Badge
        </Typography>
        <Row label="Conference ID">{intake.conference_id || DASH}</Row>
        <Row label="Organisation">{intake.organization || DASH}</Row>
        <Row label="Collected">{formatDateTime(intake.created_at)}</Row>
        {badge && (
          <Box
            component="img"
            src={badge}
            alt="Badge photo"
            sx={{ mt: 1, maxWidth: '100%', maxHeight: 240, borderRadius: 1 }}
          />
        )}
      </Box>

      <Divider />
      <Box>
        <Typography variant="overline" color="text.secondary">
          Consent
        </Typography>
        {consent ? (
          <>
            <Row label="Form">
              {consent.name} (v{consent.version})
            </Row>
            {consent.definition.choices.map((c) => (
              <Row key={c.id} label={c.label}>
                {intake.consent_answers?.choices[c.id] ?? DASH}
              </Row>
            ))}
            {intake.consent_answers?.initials && (
              <Row label="Initials">{intake.consent_answers.initials}</Row>
            )}
            <Row label="Signed name">{intake.signed_name || DASH}</Row>
            <Row label="Consented">
              {intake.consented_at ? formatDateTime(intake.consented_at) : DASH}
            </Row>
            {signature && (
              <Box
                component="img"
                src={signature}
                alt="Signature"
                sx={{
                  mt: 1,
                  maxWidth: 320,
                  width: '100%',
                  bgcolor: '#fff',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              />
            )}
            <Box>
              <Button size="small" onClick={() => setShowText((s) => !s)} sx={{ mt: 0.5 }}>
                {showText ? 'Hide the consent text' : 'Show the consent text they signed'}
              </Button>
              <Collapse in={showText} unmountOnExit>
                <ConsentText definition={consent.definition} />
              </Collapse>
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No consent recorded.
          </Typography>
        )}
      </Box>

      <Divider />
      <Box>
        <Typography variant="overline" color="text.secondary">
          Survey
        </Typography>
        {survey ? (
          <>
            <Row label="Form">
              {survey.name} (v{survey.version})
            </Row>
            {survey.definition.questions
              .filter((q) => visible(q, answers))
              .map((q) =>
                q.type === 'heading' ? (
                  <Typography key={q.id} variant="subtitle2" sx={{ mt: 1 }}>
                    {q.label}
                  </Typography>
                ) : (
                  <Row key={q.id} label={q.label}>
                    {formatAnswer(q, answers[q.id])}
                  </Row>
                ),
              )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No survey recorded.
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

export function ParticipantIntakeDialog({
  open,
  tenantId,
  participantUserId,
  participantName,
  onClose,
}: {
  open: boolean;
  tenantId: string;
  participantUserId: string;
  participantName: string;
  onClose: () => void;
}) {
  const [intakes, setIntakes] = useState<ParticipantIntake[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIntakes(null);
    setError(null);
    listParticipantIntakes(tenantId, participantUserId)
      .then((rows) => {
        if (!cancelled) setIntakes(rows);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(extractErrorMessage(e, 'could not load the intake record'));
          setIntakes([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, tenantId, participantUserId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Conference intake — {participantName}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {intakes === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : intakes.length === 0 ? (
          !error && (
            <Typography variant="body2" color="text.secondary">
              No intake recorded for this participant.
            </Typography>
          )
        ) : (
          <Stack spacing={3} divider={<Divider flexItem />}>
            {intakes.map((i) => (
              <Box key={i.id}>
                {intakes.length > 1 && (
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Intake of {formatDate(i.created_at)}
                  </Typography>
                )}
                <IntakeRecord tenantId={tenantId} intake={i} />
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
